import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { CardImage } from '../../components/CardImage';
import { CardDetailModal } from '../../components/CardDetailModal';
import {
  getGameSets,
  getGameSetsByGame,
  getGameSetById,
  getCardgames,
  getUserCardsForCollection,
  fetchCardsForCollection,
  addCardToList,
  getUserWishlistCardIds,
  getUserOfferlistCardIds,
  toggleWishlistCard,
  addCardsBatchToList,
  removeCardsBatchFromWishlist,
  removeCardsBatchFromOfferlist,
  BatchAddResult,
  findMatchingVariant
} from '../../services/supabaseService';
import { GameSet, UserCardSummary } from '../../utils/setMatcher';
import { QuickFilters } from '../../components/QuickFilters';
import { useSettings } from '../../contexts/SettingsContext';

interface CollectionCardsPageProps {
  activeGame: number | 'All';
  setActiveGame: (game: number | 'All') => void;
  dbGames: any[];
}

export const CollectionCardsPage: React.FC<CollectionCardsPageProps> = ({
  activeGame,
  setActiveGame,
  dbGames
}) => {
  const { setId, gameId: paramGameId } = useParams<{ setId?: string; gameId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const { settings, formatPrice } = useSettings();
  const cardsContainerRef = useRef<HTMLDivElement>(null);

  // Selected game
  const currentGame = useMemo(() => {
    if (paramGameId) {
      const found = dbGames.find((g: any) => 
        String(g.id) === String(paramGameId) || 
        String(g.slug || '').toLowerCase() === String(paramGameId).toLowerCase()
      );
      if (found) return found;
    }
    if (activeGame !== 'All' && activeGame) {
      const found = dbGames.find((g: any) => 
        Number(g.id) === Number(activeGame) || 
        String(g.slug || '').toLowerCase() === String(activeGame).toLowerCase()
      );
      if (found) return found;
    }
    const firstReal = dbGames.find((g: any) => g.id && !isNaN(Number(g.id)));
    return firstReal || dbGames[0] || null;
  }, [dbGames, paramGameId, activeGame]);

  const currentGameId = useMemo(() => {
    return currentGame?.id ? Number(currentGame.id) : 1;
  }, [currentGame]);

  // Set & Cards State
  const [currentSet, setCurrentSet] = useState<GameSet | null>(null);
  const [loadingSet, setLoadingSet] = useState(true);
  const [cards, setCards] = useState<any[]>([]);
  const [loadingCards, setLoadingCards] = useState(true);
  const [userCards, setUserCards] = useState<UserCardSummary[]>([]);
  const [loadingUserCards, setLoadingUserCards] = useState(false);

  // Filters & Controls
  const [searchQuery, setSearchQuery] = useState('');
  const [ownershipFilter, setOwnershipFilter] = useState<'all' | 'owned' | 'missing'>('all');
  const [rarityFilter, setRarityFilter] = useState<string>('all');
  const [activeQuickFilters, setActiveQuickFilters] = useState<Record<string, string>>({});
  const [viewMode, setViewMode] = useState<'grid' | 'checklist'>('grid');
  const [sortBy, setSortBy] = useState<'code' | 'name' | 'rarity' | 'price'>('code');

  // Pagination for weak device performance
  const [currentPage, setCurrentPage] = useState(1);
  const [cardsPerPage, setCardsPerPage] = useState<number>(36);

  // Detail Modal & Actions
  const [selectedCard, setSelectedCard] = useState<any | null>(null);
  const [addingCardId, setAddingCardId] = useState<string | number | null>(null);
  const [wishlistCardIds, setWishlistCardIds] = useState<Set<string>>(new Set());
  const [wishlistLoadingCardId, setWishlistLoadingCardId] = useState<string | null>(null);

  // Load user wishlist IDs
  const loadWishlist = useCallback(async () => {
    if (!user) {
      setWishlistCardIds(new Set());
      return;
    }
    try {
      const ids = await getUserWishlistCardIds(user.id);
      setWishlistCardIds(ids);
    } catch (err) {
      console.error('Error loading wishlist IDs:', err);
    }
  }, [user]);

  useEffect(() => {
    loadWishlist();
  }, [loadWishlist]);

  // Load user offerlist IDs
  const [offerlistCardIds, setOfferlistCardIds] = useState<Set<string>>(new Set());

  const loadOfferlist = useCallback(async () => {
    if (!user) {
      setOfferlistCardIds(new Set());
      return;
    }
    try {
      const ids = await getUserOfferlistCardIds(user.id);
      setOfferlistCardIds(ids);
    } catch (err) {
      console.error('Error loading offerlist IDs:', err);
    }
  }, [user]);

  useEffect(() => {
    loadOfferlist();
  }, [loadOfferlist]);

  const isCardInOfferlist = useCallback((card: any) => {
    if (!user || !card) return false;
    const cid = String(card.id || '');
    const cext = String(card.external_id || '');
    const ccode = String(card.code || '').toLowerCase();
    return (
      (cid && offerlistCardIds.has(cid)) ||
      (cext && offerlistCardIds.has(cext)) ||
      (ccode && offerlistCardIds.has(ccode))
    );
  }, [user, offerlistCardIds]);

  // Reset pagination on filter or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, ownershipFilter, rarityFilter, sortBy, activeQuickFilters]);

  // Reset quick filters when set or game changes
  useEffect(() => {
    setActiveQuickFilters({});
  }, [setId, currentGameId]);

  // 1. Fetch Set info
  useEffect(() => {
    let isMounted = true;
    const loadSet = async () => {
      setLoadingSet(true);
      try {
        if (!setId) return;

        // 1. Try single set lookup first by ID/code/slug (instant, direct, exact)
        let single = await getGameSetById(setId, paramGameId);
        if (!single) {
          single = await getGameSetById(setId);
        }
        if (single && isMounted) {
          setCurrentSet(single);
          if (single.game_id && Number(single.game_id) !== currentGameId) {
            setActiveGame(Number(single.game_id));
          }
          return;
        }

        // 2. Try sets for this specific game
        const setsForGame = await getGameSetsByGame(paramGameId || currentGameId);
        const foundInGame = (setsForGame as any[]).find((s: any) => {
          if (String(s.id) === String(setId)) return true;
          if ((s.code || '').toUpperCase() === setId.toUpperCase()) return true;
          if (s.slug === setId) return true;
          return false;
        });

        if (foundInGame && isMounted) {
          setCurrentSet(foundInGame);
          if (foundInGame.game_id && Number(foundInGame.game_id) !== currentGameId) {
            setActiveGame(Number(foundInGame.game_id));
          }
          return;
        }

        // 3. Fallback to all sets
        const sets = await getGameSets();
        if (!isMounted) return;

        // Find set by ID or by code/slug
        const found = (sets as any[]).find((s: any) => {
          if (setId && String(s.id) === String(setId)) return true;
          if (setId && (s.code || '').toUpperCase() === setId.toUpperCase()) return true;
          if (setId && s.slug === setId) return true;
          return false;
        });

        if (found) {
          setCurrentSet(found);
          if (found.game_id && Number(found.game_id) !== currentGameId) {
            setActiveGame(Number(found.game_id));
          }
        }
      } catch (err) {
        console.error('Error loading set:', err);
      } finally {
        if (isMounted) setLoadingSet(false);
      }
    };

    loadSet();
    return () => {
      isMounted = false;
    };
  }, [setId, currentGameId, paramGameId, setActiveGame]);

  // 2. Fetch User's cards for this game
  const loadUserCards = useCallback(async () => {
    if (!user) {
      setUserCards([]);
      return;
    }
    setLoadingUserCards(true);
    try {
      const uCards = await getUserCardsForCollection(user.id, currentGameId);
      setUserCards(uCards || []);
    } catch (err) {
      console.error('Error loading user collection cards:', err);
    } finally {
      setLoadingUserCards(false);
    }
  }, [user, currentGameId]);

  useEffect(() => {
    loadUserCards();
  }, [loadUserCards]);

  // 3. Fetch cards belonging to this collection
  useEffect(() => {
    let isMounted = true;
    const loadCollectionCards = async () => {
      if (!currentSet) return;
      if (currentSet.maintenance) {
        setLoadingCards(false);
        setCards([]);
        return;
      }
      setLoadingCards(true);

      try {
        const gameTarget = currentSet.game_id || currentGame?.slug || currentGameId;
        const res = await fetchCardsForCollection(
          gameTarget,
          {
            id: currentSet.id,
            code: currentSet.code,
            name: currentSet.name,
            slug: currentSet.slug,
            external_id: currentSet.external_id
          },
          1,
          200
        );

        if (isMounted) {
          setCards(res.data || []);
        }
      } catch (err) {
        console.error('Error fetching cards for set:', err);
      } finally {
        if (isMounted) setLoadingCards(false);
      }
    };

    if (currentSet) {
      loadCollectionCards();
    }
  }, [currentSet, currentGame, currentGameId]);

  // Map user cards to this set's cards for fast lookup by unique card ID (strictly avoiding code-based collisions)
  const userCardMap = useMemo(() => {
    const map = new Map<string, { quantity: number; user_card_id?: string | number }>();

    userCards.forEach(uc => {
      const extId = (uc.external_id ? String(uc.external_id) : '').trim().toUpperCase();
      const cardDbId = (uc.card_id !== undefined && uc.card_id !== null ? String(uc.card_id) : (uc.id ? String(uc.id) : '')).trim();

      // 1. Mapeia pelo ID único da carta (external_id da API)
      if (extId) {
        const prev = map.get(`id:${extId}`);
        map.set(`id:${extId}`, {
          quantity: (prev ? prev.quantity : 0) + (uc.quantity || 1),
          user_card_id: uc.user_card_id
        });
      }

      // 2. Mapeia pelo ID de banco (card_id)
      if (cardDbId) {
        const prev = map.get(`db:${cardDbId}`);
        map.set(`db:${cardDbId}`, {
          quantity: (prev ? prev.quantity : 0) + (uc.quantity || 1),
          user_card_id: uc.user_card_id
        });
      }
    });

    return map;
  }, [userCards]);

  // Check how many copies of card the user owns (strictly by unique ID)
  const getCardOwnedInfo = useCallback((card: any) => {
    if (!user) return { isOwned: false, quantity: 0 };

    const cardId = (card.id !== undefined && card.id !== null ? String(card.id) : '').trim().toUpperCase();
    const cardExtId = (card.external_id ? String(card.external_id) : '').trim().toUpperCase();
    const cardDbId = (card.card_id || card.card_db_id ? String(card.card_id || card.card_db_id) : (typeof card.id === 'number' ? String(card.id) : '')).trim();

    // 1. Prioridade máxima: ID único da carta (card.id ou card.external_id)
    if (cardId && userCardMap.has(`id:${cardId}`)) {
      return { isOwned: true, quantity: userCardMap.get(`id:${cardId}`)!.quantity };
    }
    if (cardExtId && userCardMap.has(`id:${cardExtId}`)) {
      return { isOwned: true, quantity: userCardMap.get(`id:${cardExtId}`)!.quantity };
    }

    // 2. ID primário do banco de dados (se disponível)
    if (cardDbId && userCardMap.has(`db:${cardDbId}`)) {
      return { isOwned: true, quantity: userCardMap.get(`db:${cardDbId}`)!.quantity };
    }

    return { isOwned: false, quantity: 0 };
  }, [user, userCardMap]);

  // Summary Metrics
  const collectionStats = useMemo(() => {
    const totalCardsCount = cards.length > 0 ? cards.length : (currentSet?.cards_included || 0);
    let uniqueOwnedCount = 0;
    let totalCopiesCount = 0;

    cards.forEach(c => {
      const info = getCardOwnedInfo(c);
      if (info.isOwned && info.quantity > 0) {
        uniqueOwnedCount += 1;
        totalCopiesCount += info.quantity;
      }
    });

    const completionRate = totalCardsCount > 0
      ? Math.min(100, Math.round((uniqueOwnedCount / totalCardsCount) * 100))
      : 0;
    const missingCount = Math.max(0, totalCardsCount - uniqueOwnedCount);

    return {
      totalCardsCount,
      uniqueOwnedCount,
      totalCopiesCount,
      completionRate,
      missingCount,
      isComplete: completionRate === 100
    };
  }, [cards, currentSet, getCardOwnedInfo]);

  // Available rarities for filter
  const availableRarities = useMemo(() => {
    const rarities = new Set<string>();
    cards.forEach(c => {
      const r = c.rarity || c.set_rarity;
      if (r && typeof r === 'string' && r.trim()) {
        rarities.add(r.trim());
      }
    });
    return Array.from(rarities).sort();
  }, [cards]);

  // Filtered & Sorted Cards
  const filteredCards = useMemo(() => {
    return cards
      .filter(card => {
        const ownedInfo = getCardOwnedInfo(card);

        // 1. Ownership filter
        if (ownershipFilter === 'owned' && !ownedInfo.isOwned) return false;
        if (ownershipFilter === 'missing' && ownedInfo.isOwned) return false;

        // 2. Rarity filter
        if (rarityFilter !== 'all') {
          const r = (card.rarity || card.set_rarity || '').toLowerCase();
          if (r !== rarityFilter.toLowerCase()) return false;
        }

        // 3. Search query
        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase().trim();
          const nameMatch = (card.name || '').toLowerCase().includes(q);
          const codeMatch = (card.code || card.external_id || '').toLowerCase().includes(q);
          const typeMatch = (card.type || card.cardType || '').toLowerCase().includes(q);
          const rarityMatch = (card.rarity || '').toLowerCase().includes(q);
          if (!nameMatch && !codeMatch && !typeMatch && !rarityMatch) return false;
        }

        // 4. Quick Filters
        for (const [field, filterVal] of Object.entries(activeQuickFilters)) {
          if (!filterVal || !filterVal.trim()) continue;
          const normalizeStr = (val: string) => val.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
          const expectedNorm = normalizeStr(filterVal);

          let cardFieldVal: any = null;
          if (field === 'color' || field === 'colors') {
            cardFieldVal = card.colors || card.color || card.colorIdentity;
          } else if (field === 'cardType' || field === 'type' || field === 'card_type') {
            cardFieldVal = card.cardType || card.card_type || card.type || card.types;
          } else if (field === 'category') {
            cardFieldVal = card.category || card.supertype || card.super || card.pokemon?.card_type || card.cardType || card.type;
          } else if (field === 'pokemonType') {
            cardFieldVal = card.pokemonType || card.types || card.pokemon?.types || card.type || card.subtypes;
          } else if (field === 'stage') {
            cardFieldVal = card.stage || card.pokemon?.stage || card.subtype;
          } else if (field === 'rarity') {
            cardFieldVal = card.rarity || card.set_rarity || card.rarity_code || card.md_rarity;
          } else if (field === 'attribute') {
            cardFieldVal = card.attribute;
          } else if (field === 'element') {
            cardFieldVal = card.element || card.elements;
          } else if (field === 'grade') {
            cardFieldVal = card.grade;
          } else if (field === 'level' || field === 'bloomLevel') {
            cardFieldVal = card.level ?? card.bloomLevel;
          } else if (field === 'form') {
            cardFieldVal = card.form;
          } else if (field === 'faction') {
            cardFieldVal = card.faction;
          } else if (field === 'nation') {
            cardFieldVal = card.nation;
          } else if (field === 'class' || field === 'classes') {
            cardFieldVal = card.class || card.classes;
          } else if (field === 'frameType') {
            cardFieldVal = card.frameType;
          } else if (field === 'arena') {
            cardFieldVal = card.arena || card.arenas;
          } else if (field === 'super') {
            cardFieldVal = card.super || card.supertype || card.supertypes;
          } else {
            cardFieldVal = card[field] ?? card[field.toLowerCase()];
          }

          if (cardFieldVal === undefined || cardFieldVal === null) return false;

          if (Array.isArray(cardFieldVal)) {
            const hasMatch = cardFieldVal.some(v => {
              const s = normalizeStr(String(v));
              return s === expectedNorm || s.includes(expectedNorm);
            });
            if (!hasMatch) return false;
          } else {
            const strVal = normalizeStr(String(cardFieldVal));
            if (strVal !== expectedNorm && !strVal.includes(expectedNorm)) {
              return false;
            }
          }
        }

        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'name') {
          return (a.name || '').localeCompare(b.name || '');
        }
        if (sortBy === 'rarity') {
          return (a.rarity || '').localeCompare(b.rarity || '');
        }
        if (sortBy === 'price') {
          return (b.price || 0) - (a.price || 0);
        }
        // Default sort by code/number
        const codeA = a.code || a.external_id || '';
        const codeB = b.code || b.external_id || '';
        const codeComp = codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
        if (codeComp !== 0) return codeComp;
        // Se o código for idêntico, desempata pelo ID único da carta
        const idA = String(a.id || a.external_id || '');
        const idB = String(b.id || b.external_id || '');
        return idA.localeCompare(idB, undefined, { numeric: true, sensitivity: 'base' });
      });
  }, [cards, ownershipFilter, rarityFilter, searchQuery, sortBy, getCardOwnedInfo, activeQuickFilters]);

  // Derived pagination calculations
  const totalPages = Math.max(1, Math.ceil(filteredCards.length / cardsPerPage));
  const paginatedCards = useMemo(() => {
    if (cardsPerPage >= filteredCards.length) return filteredCards;
    const start = (currentPage - 1) * cardsPerPage;
    return filteredCards.slice(start, start + cardsPerPage);
  }, [filteredCards, currentPage, cardsPerPage]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    if (cardsContainerRef.current) {
      cardsContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Helper to prioritize card variant information matching the set code displayed on the page
  const prioritizeCardVariantForSet = useCallback((card: any, targetSetCode?: string, targetSetName?: string) => {
    if (!card) return card;
    const tCode = (targetSetCode || '').trim();
    const tName = (targetSetName || '').trim();
    const normCode = tCode.toLowerCase();
    const cleanCode = normCode.replace(/[-_]/g, '');
    const normName = tName.toLowerCase();

    const variantsList: any[] = Array.isArray(card.variants) && card.variants.length > 0
      ? card.variants
      : (Array.isArray(card.raw?.variants) ? card.raw.variants : []);

    if (variantsList.length === 0) {
      if (tCode && (!card.set_code || card.set_code === 'EXP')) {
        return {
          ...card,
          set_code: tCode,
          setCode: tCode
        };
      }
      return card;
    }

    // Find the variant that best corresponds to the set code displayed on the page
    let matchingIndex = -1;
    let matchingVariant: any = null;

    for (let i = 0; i < variantsList.length; i++) {
      const v = variantsList[i];
      if (!v) continue;
      const vSet = typeof v.set === 'object' && v.set ? v.set : null;
      const vCode = String(v.set_code || v.setCode || vSet?.set_code || vSet?.code || '').toLowerCase().trim();
      const vCleanCode = vCode.replace(/[-_]/g, '');
      const vVarCode = String(v.variant_code || v.variantCode || v.code || '').toLowerCase().trim();
      const vCleanVarCode = vVarCode.replace(/[-_]/g, '');
      const vName = String(v.set_name || v.setName || vSet?.name || vSet?.set_name || vSet?.beauty_name || (typeof v.set === 'string' ? v.set : '') || '').toLowerCase().trim();

      // 1. Direct set code match
      if (normCode && (vCode === normCode || vCleanCode === cleanCode)) {
        matchingVariant = v;
        matchingIndex = i;
        break;
      }

      // 2. Variant code starts with set code or matches
      if (normCode && (
        vVarCode.startsWith(normCode + '-') || 
        vVarCode.startsWith(normCode + '_') || 
        vCleanVarCode.startsWith(cleanCode) ||
        vVarCode.includes(normCode)
      )) {
        matchingVariant = v;
        matchingIndex = i;
        break;
      }

      // 3. Set name match
      if (normName && (vName === normName || vName.includes(normName) || normName.includes(vName))) {
        matchingVariant = v;
        matchingIndex = i;
        break;
      }
    }

    // Fallback to findMatchingVariant helper or first variant
    if (!matchingVariant) {
      matchingVariant = findMatchingVariant(card, tCode, tName) || variantsList[0];
      matchingIndex = variantsList.indexOf(matchingVariant);
      if (matchingIndex === -1) matchingIndex = 0;
    }

    if (!matchingVariant) {
      const rawVar = card.variant_number !== undefined && card.variant_number !== null
        ? card.variant_number
        : (card.variantNumber !== undefined && card.variantNumber !== null ? card.variantNumber : 1);
      const vNum = Number(rawVar) > 0 ? Number(rawVar) : 1;
      return {
        ...card,
        variant_number: vNum
      };
    }

    const variantNum = Number(
      matchingVariant.variant_number ?? 
      matchingVariant.variantNumber ?? 
      (matchingIndex >= 0 ? matchingIndex + 1 : 1)
    );
    const variantCode = matchingVariant.variant_code || matchingVariant.variantCode || matchingVariant.code || null;
    const variantExtId = matchingVariant.variant_external_id || matchingVariant.variantExternalId || null;
    const variantSetCode = matchingVariant.set_code || matchingVariant.setCode || 
      (typeof matchingVariant.set === 'string' ? matchingVariant.set : matchingVariant.set?.set_code || matchingVariant.set?.code) || 
      tCode || null;

    // tcgplayer_id DEVE vir estritamente da matchingVariant correspondente. Se a variante não tiver, DEVE FICAR NULL!
    // NUNCA deve herdar tcgplayer_id de outra variante ou do objeto raiz da carta!
    const rawMatchingTcg = matchingVariant.tcgplayer_id ?? matchingVariant.tcgplayerId ?? matchingVariant.tcgPlayerId ?? matchingVariant.tcgplayer?.productId ?? matchingVariant.tcgplayer?.id;
    const variantTcgId = (rawMatchingTcg !== undefined && rawMatchingTcg !== null && String(rawMatchingTcg).trim() !== '' && String(rawMatchingTcg).trim() !== '0' && String(rawMatchingTcg).trim() !== 'null' && String(rawMatchingTcg).trim() !== 'undefined')
      ? String(rawMatchingTcg).trim()
      : null;

    const variantRarity = matchingVariant.rarity || matchingVariant.set_rarity || matchingVariant.card_rarity || card.rarity || null;
    const variantImg = matchingVariant.image_url || matchingVariant.imageUrl || 
      (variantTcgId ? `https://tcgplayer-cdn.tcgplayer.com/product/${variantTcgId}_in_1000x1000.jpg` : null) || 
      card.imageUrl || card.image_url;
    const variantPrice = (typeof matchingVariant.price === 'number' && matchingVariant.price > 0) ? matchingVariant.price : card.price;
    const variantRawPrice = (typeof matchingVariant.raw_price === 'number' && matchingVariant.raw_price > 0) ? matchingVariant.raw_price : card.raw_price;
    const variantIsFoil = matchingVariant.is_foil !== undefined 
      ? Boolean(matchingVariant.is_foil) 
      : (matchingVariant.finish === 'foil' || matchingVariant.foiling === 'foil' || Boolean(card.is_foil));

    const updatedVariantObj = {
      ...matchingVariant,
      tcgplayer_id: variantTcgId,
      tcgplayerId: variantTcgId
    };

    return {
      ...card,
      variant: updatedVariantObj,
      selectedVariant: updatedVariantObj,
      variant_number: variantNum,
      variantNumber: variantNum,
      variant_code: variantCode,
      variantCode: variantCode,
      variant_external_id: variantExtId,
      variantExternalId: variantExtId,
      set_code: variantSetCode,
      setCode: variantSetCode,
      tcgplayer_id: variantTcgId,
      tcgplayerId: variantTcgId,
      rarity: variantRarity,
      image_url: variantImg,
      imageUrl: variantImg,
      image: variantImg,
      price: variantPrice,
      raw_price: variantRawPrice,
      rawPrice: variantRawPrice,
      is_foil: variantIsFoil,
      isFoil: variantIsFoil
    };
  }, []);

  // Add card to user's collection
  const handleQuickAdd = async (card: any, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!user) {
      navigate('/login');
      return;
    }

    const cardIdentifier = String(card.id || card.external_id || card.code || '');
    setAddingCardId(cardIdentifier);

    try {
      const displayedSetCode = (currentSet?.code || setId || '').trim();
      const displayedSetName = (currentSet?.name || '').trim();
      const cardToAdd = prioritizeCardVariantForSet(card, displayedSetCode, displayedSetName);
      await addCardToList(user.id, 'cards', cardToAdd, 1);
      showNotification(`"${card.name}" adicionada à sua coleção!`, 'success');
      // Refresh user's cards
      await loadUserCards();
    } catch (err: any) {
      console.error('Error adding card to collection:', err);
      showNotification(err.message || 'Erro ao adicionar carta', 'error');
    } finally {
      setAddingCardId(null);
    }
  };

  // Check if a card is in the user's wishlist
  const isCardInWishlist = useCallback((card: any) => {
    if (!user || !card) return false;
    const cid = String(card.id || '');
    const cext = String(card.external_id || '');
    const ccode = String(card.code || '').toLowerCase();
    return (
      (cid && wishlistCardIds.has(cid)) ||
      (cext && wishlistCardIds.has(cext)) ||
      (ccode && wishlistCardIds.has(ccode))
    );
  }, [user, wishlistCardIds]);

  // Toggle card in user's wishlist
  const handleToggleWishlist = async (card: any, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!user) {
      navigate('/login');
      return;
    }

    const cardIdentifier = String(card.id || card.external_id || card.code || '');
    setWishlistLoadingCardId(cardIdentifier);

    try {
      const displayedSetCode = (currentSet?.code || setId || '').trim();
      const displayedSetName = (currentSet?.name || '').trim();
      const cardToToggle = prioritizeCardVariantForSet(card, displayedSetCode, displayedSetName);
      const res = await toggleWishlistCard(user.id, cardToToggle);
      if (res.added) {
        showNotification(`"${card.name}" adicionada à Lista de Desejos!`, 'success');
      } else {
        showNotification(`"${card.name}" removida da Lista de Desejos.`, 'info');
      }
      await loadWishlist();
    } catch (err: any) {
      console.error('Error toggling wishlist:', err);
      showNotification(err.message || 'Erro ao atualizar Lista de Desejos', 'error');
    } finally {
      setWishlistLoadingCardId(null);
    }
  };

  // Batch Operations & Multi-Select State
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedCardKeys, setSelectedCardKeys] = useState<Set<string>>(new Set());
  const [showBatchMenu, setShowBatchMenu] = useState(false);
  const batchMenuRef = useRef<HTMLDivElement>(null);

  // Batch Modal & Execution State
  const [batchModal, setBatchModal] = useState<{
    isOpen: boolean;
    type: 'collection' | 'wishlist' | 'offerlist' | 'remove_wishlist' | 'remove_offerlist';
    title: string;
    description: string;
    cardsToProcess: any[];
    onlyMissingOption?: boolean;
    onlyMissingChecked?: boolean;
  } | null>(null);

  const [batchLoading, setBatchLoading] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number; percent: number } | null>(null);
  const [batchResult, setBatchResult] = useState<BatchAddResult | { removed: number; total: number } | null>(null);

  // Close batch dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (batchMenuRef.current && !batchMenuRef.current.contains(e.target as Node)) {
        setShowBatchMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getCardKey = useCallback((card: any) => {
    return String(card.id || card.external_id || card.code || card.name || '');
  }, []);

  const toggleSelectCard = useCallback((card: any, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const key = getCardKey(card);
    setSelectedCardKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, [getCardKey]);

  const selectAllFiltered = useCallback(() => {
    const allKeys = new Set<string>();
    filteredCards.forEach(c => allKeys.add(getCardKey(c)));
    setSelectedCardKeys(allKeys);
  }, [filteredCards, getCardKey]);

  const clearSelection = useCallback(() => {
    setSelectedCardKeys(new Set());
  }, []);

  const openBatchModal = useCallback((
    type: 'collection' | 'wishlist' | 'offerlist' | 'remove_wishlist' | 'remove_offerlist',
    targetCards: any[],
    title: string,
    description: string,
    options?: { onlyMissingOption?: boolean; onlyMissingChecked?: boolean }
  ) => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!targetCards || targetCards.length === 0) {
      showNotification('Nenhuma carta encontrada para esta operação.', 'info');
      return;
    }

    const displayedSetCode = (currentSet?.code || setId || '').trim();
    const displayedSetName = (currentSet?.name || '').trim();
    const prioritizedCards = targetCards.map(c => 
      prioritizeCardVariantForSet(c, displayedSetCode, displayedSetName)
    );

    setShowBatchMenu(false);
    setBatchProgress(null);
    setBatchResult(null);
    setBatchModal({
      isOpen: true,
      type,
      title,
      description,
      cardsToProcess: prioritizedCards,
      onlyMissingOption: options?.onlyMissingOption,
      onlyMissingChecked: options?.onlyMissingChecked ?? false
    });
  }, [user, navigate, showNotification, currentSet, setId, prioritizeCardVariantForSet]);

  const executeBatchAction = useCallback(async () => {
    if (!user || !batchModal) return;

    setBatchLoading(true);
    setBatchProgress({ current: 0, total: batchModal.cardsToProcess.length, percent: 0 });
    setBatchResult(null);

    const displayedSetCode = (currentSet?.code || setId || '').trim();
    const displayedSetName = (currentSet?.name || '').trim();
    const prioritizedCards = batchModal.cardsToProcess.map(c => 
      prioritizeCardVariantForSet(c, displayedSetCode, displayedSetName)
    );

    try {
      if (batchModal.type === 'remove_wishlist') {
        const removed = await removeCardsBatchFromWishlist(user.id, prioritizedCards);
        setBatchResult({ removed, total: prioritizedCards.length });
        await loadWishlist();
        showNotification(`${removed} carta(s) removida(s) da Lista de Desejos!`, 'success');
        if (isSelectionMode) clearSelection();
      } else if (batchModal.type === 'remove_offerlist') {
        const removed = await removeCardsBatchFromOfferlist(user.id, prioritizedCards);
        setBatchResult({ removed, total: prioritizedCards.length });
        await loadOfferlist();
        showNotification(`${removed} carta(s) removida(s) da Lista de Ofertas!`, 'success');
        if (isSelectionMode) clearSelection();
      } else {
        const targetList = batchModal.type === 'collection' ? 'cards' : (batchModal.type === 'wishlist' ? 'wishlist' : 'offerlist');
        const onlyMissing = Boolean(batchModal.onlyMissingChecked);

        const res = await addCardsBatchToList(
          user.id,
          targetList,
          prioritizedCards,
          {
            onlyMissing,
            setCode: displayedSetCode,
            onProgress: (current, total) => {
              const percent = total > 0 ? Math.round((current / total) * 100) : 0;
              setBatchProgress({ current, total, percent });
            }
          }
        );

        setBatchResult(res);

        if (targetList === 'cards') {
          await loadUserCards();
          showNotification(
            `${res.added} carta(s) adicionada(s) à sua coleção com sucesso!`,
            'success'
          );
        } else if (targetList === 'wishlist') {
          await loadWishlist();
          showNotification(
            `${res.added} carta(s) adicionada(s) à sua Lista de Desejos!`,
            'success'
          );
        } else {
          await loadOfferlist();
          showNotification(
            `${res.added} carta(s) adicionada(s) à sua Lista de Ofertas (Trocas)!`,
            'success'
          );
        }

        if (isSelectionMode) clearSelection();
      }
    } catch (err: any) {
      console.error('Error executing batch action:', err);
      showNotification(err?.message || 'Erro ao processar operação em lote.', 'error');
    } finally {
      setBatchLoading(false);
    }
  }, [user, batchModal, isSelectionMode, clearSelection, loadWishlist, loadOfferlist, loadUserCards, showNotification, currentSet, setId, prioritizeCardVariantForSet]);

  const formattedReleaseDate = useMemo(() => {
    if (!currentSet?.release_date) return null;
    try {
      const [year, month, day] = currentSet.release_date.split('-');
      if (year && month && day) {
        return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: 'long',
          year: 'numeric'
        });
      }
    } catch {
      // fallback
    }
    return currentSet.release_date;
  }, [currentSet]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-8 space-y-6 max-w-7xl mx-auto">
      {/* Top Breadcrumb & Navigation */}
      <div className="flex flex-wrap items-center justify-between gap-4 pb-2 border-b border-slate-800/80">
        <div className="flex items-center gap-2 text-xs">
          <Link
            to={`/colecoes/${currentGameId}`}
            className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors font-medium px-2.5 py-1.5 rounded-lg hover:bg-slate-900"
          >
            <i className="fa-solid fa-arrow-left text-[11px]"></i>
            <span>Voltar para Coleções</span>
          </Link>
          <span className="text-slate-600">/</span>
          <span className="text-slate-400 font-medium">
            {currentGame?.name || 'Jogo'}
          </span>
          <span className="text-slate-600">/</span>
          <span className="text-purple-400 font-bold truncate max-w-[200px] sm:max-w-[300px]">
            {currentSet?.name || 'Carregando coleção...'}
          </span>
        </div>

        {/* Quick Link to Browse All Sets */}
        <Link
          to={`/colecoes/${currentGameId}`}
          className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1 font-semibold"
        >
          <i className="fa-solid fa-layer-group text-xs"></i>
          <span>Ver outras coleções</span>
        </Link>
      </div>

      {/* Hero / Header Card of the Collection */}
      {loadingSet ? (
        <div className="animate-pulse bg-slate-900/60 border border-slate-800 rounded-3xl p-6 md:p-8 h-48"></div>
      ) : currentSet ? (
        <div className="bg-gradient-to-br from-slate-900 via-slate-900/90 to-purple-950/20 border border-purple-500/20 rounded-3xl p-6 md:p-8 shadow-xl shadow-black/40 relative overflow-hidden">
          {/* Subtle decorative glow */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20"></div>

          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            {/* Left Column: Set Details */}
            <div className="space-y-3 max-w-2xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm font-black px-3 py-1 rounded-xl bg-purple-950/90 text-purple-300 border border-purple-500/40 tracking-wider shadow-sm">
                  {currentSet.code || 'EXP'}
                </span>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-xl bg-slate-800/80 text-slate-300 border border-slate-700/60">
                  {currentGame?.name || 'TCG'}
                </span>
                {currentSet.temp_type && (
                  <span className="text-xs font-medium px-2.5 py-1 rounded-xl bg-slate-800/50 text-slate-400">
                    {currentSet.temp_type}
                  </span>
                )}
                {formattedReleaseDate && (
                  <span className="text-xs text-slate-400 flex items-center gap-1.5 ml-1">
                    <i className="fa-regular fa-calendar text-[11px]"></i>
                    {formattedReleaseDate}
                  </span>
                )}
                {currentSet.maintenance && (
                  <span className="text-xs font-bold px-2.5 py-1 rounded-xl bg-amber-500/20 text-amber-300 border border-amber-500/35 flex items-center gap-1.5 shadow-sm">
                    <i className="fa-solid fa-wrench text-[10px] animate-pulse"></i>
                    <span>Em Manutenção</span>
                  </span>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-black text-white tracking-tight leading-tight">
                {currentSet.name}
              </h1>

              <p className="text-xs md:text-sm text-slate-400">
                {currentSet.maintenance
                  ? 'Esta coleção está temporariamente indisponível para consulta pois suas cartas estão sendo atualizadas e catalogadas.'
                  : 'Visualizando o catálogo oficial completo desta coleção. Marque as cartas que você possui e descubra quais faltam para completar seu set.'}
              </p>
            </div>

            {/* Right Column: Collector Progress Card */}
            <div className="bg-slate-950/80 border border-slate-800 rounded-2xl p-5 lg:w-80 shrink-0 shadow-lg space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                  <i className="fa-solid fa-trophy text-amber-400 text-xs"></i>
                  Seu Progresso
                </span>
                {user && (
                  <span
                    className={`text-xs font-black px-2 py-0.5 rounded-lg ${
                      collectionStats.isComplete
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                        : collectionStats.uniqueOwnedCount > 0
                        ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                        : 'bg-slate-800 text-slate-400'
                    }`}
                  >
                    {collectionStats.completionRate}%
                  </span>
                )}
              </div>

              {user ? (
                <>
                  {/* Progress Bar */}
                  <div className="space-y-1.5">
                    <div className="w-full bg-slate-900 rounded-full h-3 overflow-hidden border border-slate-800">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${
                          collectionStats.isComplete
                            ? 'bg-emerald-400 shadow-sm shadow-emerald-500/50'
                            : collectionStats.uniqueOwnedCount > 0
                            ? 'bg-gradient-to-r from-purple-500 to-indigo-400'
                            : 'bg-slate-800'
                        }`}
                        style={{ width: `${Math.max(0, Math.min(100, collectionStats.completionRate))}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-slate-400">
                      <span>
                        <strong className="text-white font-bold">{collectionStats.uniqueOwnedCount}</strong> de {collectionStats.totalCardsCount} únicas
                      </span>
                      <span>
                        <strong className="text-slate-300">{collectionStats.missingCount}</strong> restantes
                      </span>
                    </div>
                  </div>

                  {/* Badges Grid */}
                  <div className="grid grid-cols-2 gap-2 pt-1 text-center">
                    <div className="bg-slate-900/90 border border-slate-800/80 rounded-xl p-2">
                      <span className="block text-[10px] font-semibold text-slate-400">Total Possuído</span>
                      <span className="text-sm font-bold text-emerald-400">
                        {collectionStats.totalCopiesCount} <span className="text-[10px] text-slate-400 font-normal">cópias</span>
                      </span>
                    </div>
                    <div className="bg-slate-900/90 border border-slate-800/80 rounded-xl p-2">
                      <span className="block text-[10px] font-semibold text-slate-400">Faltando</span>
                      <span className="text-sm font-bold text-amber-400">
                        {collectionStats.missingCount} <span className="text-[10px] text-slate-400 font-normal">cartas</span>
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="space-y-3 text-center py-1">
                  <p className="text-xs text-slate-400">
                    Faça login para rastrear automaticamente as cartas que você possui desta expansão!
                  </p>
                  <Link
                    to="/login"
                    className="inline-block w-full py-2 px-3 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-all shadow-sm shadow-purple-600/30"
                  >
                    Entrar para Rastrear
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 text-center space-y-3">
          <i className="fa-solid fa-triangle-exclamation text-amber-400 text-3xl"></i>
          <h2 className="text-lg font-bold text-white">Coleção não encontrada</h2>
          <p className="text-xs text-slate-400">
            Não encontramos os detalhes desta coleção. Retorne à lista de coleções.
          </p>
          <Link
            to={`/colecoes/${currentGameId}`}
            className="inline-block px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold"
          >
            Voltar para Coleções
          </Link>
        </div>
      )}

      {/* If collection is in maintenance, show dedicated blocking screen */}
      {currentSet?.maintenance ? (
        <div className="bg-slate-900/80 border border-amber-500/30 rounded-3xl p-8 md:p-14 text-center space-y-6 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-80 h-80 bg-amber-500/5 rounded-full blur-3xl pointer-events-none"></div>
          <div className="w-20 h-20 bg-amber-500/10 border border-amber-500/25 rounded-2xl flex items-center justify-center text-amber-400 mx-auto shadow-xl">
            <i className="fa-solid fa-screwdriver-wrench text-3xl"></i>
          </div>

          <div className="space-y-3 max-w-lg mx-auto">
            <span className="text-xs font-mono font-bold tracking-widest text-amber-400 uppercase bg-amber-500/15 border border-amber-500/30 px-3 py-1 rounded-lg inline-block">
              Coleção Bloqueada
            </span>
            <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight">
              {currentSet.name} está em Manutenção
            </h2>
            <p className="text-sm text-slate-400 leading-relaxed">
              Estamos atualizando o catálogo e as cartas desta coleção no momento. O acesso a este set está temporariamente bloqueado para garantir a integridade dos dados e estará liberado assim que os dados forem concluídos.
            </p>
          </div>

          <div className="pt-2 flex flex-wrap items-center justify-center gap-3">
            <Link
              to={`/colecoes/${currentGameId}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-all shadow-lg shadow-purple-600/25"
            >
              <i className="fa-solid fa-arrow-left text-xs"></i>
              <span>Voltar para Lista de Coleções</span>
            </Link>
          </div>
        </div>
      ) : (
        <>
          {/* Simple Toolbar: Search, Filters & View Toggle */}
          <div className="bg-slate-900/80 border border-slate-800/80 rounded-2xl p-3 md:p-4 space-y-3">
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
          {/* Quick Search Input */}
          <div className="relative flex-1">
            <i className="fa-solid fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs pointer-events-none"></i>
            <input
              type="text"
              placeholder="Filtrar por nome ou código nesta coleção (ex: Luffy, 002, Rare)..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-8 py-2.5 bg-slate-950/80 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white p-1 text-xs"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            )}
          </div>

          {/* Quick Filter Buttons & View Mode */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Rarity Dropdown */}
            {availableRarities.length > 0 && (
              <div className="relative">
                <select
                  value={rarityFilter}
                  onChange={e => setRarityFilter(e.target.value)}
                  className="bg-slate-950/80 border border-slate-800 text-slate-200 text-xs rounded-xl px-3 py-2.5 focus:outline-none focus:border-purple-500 cursor-pointer"
                >
                  <option value="all">Todas as Raridades</option>
                  {availableRarities.map(r => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Sort Dropdown */}
            <div className="relative">
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value as any)}
                className="bg-slate-950/80 border border-slate-800 text-slate-200 text-xs rounded-xl px-3 py-2.5 focus:outline-none focus:border-purple-500 cursor-pointer"
              >
                <option value="code">Ordem do Set (Nº)</option>
                <option value="name">Nome (A - Z)</option>
                <option value="rarity">Raridade</option>
                <option value="price">Maior Preço</option>
              </select>
            </div>

            {/* View Mode Toggle: Grid vs Checklist */}
            <div className="flex items-center bg-slate-950/80 border border-slate-800 rounded-xl p-0.5">
              <button
                onClick={() => setViewMode('grid')}
                title="Visualização em Grade com Imagens"
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
                  viewMode === 'grid'
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <i className="fa-solid fa-grip text-xs"></i>
                <span className="hidden sm:inline">Grade</span>
              </button>
              <button
                onClick={() => setViewMode('checklist')}
                title="Visualização em Lista / Checklist de Álbum"
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
                  viewMode === 'checklist'
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <i className="fa-solid fa-list-check text-xs"></i>
                <span className="hidden sm:inline">Checklist</span>
              </button>
            </div>

            {/* Batch Actions Dropdown */}
            <div className="relative" ref={batchMenuRef}>
              <button
                type="button"
                onClick={() => setShowBatchMenu(prev => !prev)}
                className={`px-3 py-2 rounded-xl text-xs font-bold flex items-center gap-2 border transition-all cursor-pointer ${
                  showBatchMenu || isSelectionMode
                    ? 'bg-purple-600 text-white border-purple-500 shadow-md shadow-purple-600/30'
                    : 'bg-slate-950/80 text-slate-200 border-slate-800 hover:border-purple-500/50 hover:text-white'
                }`}
                title="Operações em lote para esta coleção"
              >
                <i className="fa-solid fa-wand-magic-sparkles text-xs"></i>
                <span className="hidden sm:inline">Ações em Lote</span>
                <i className={`fa-solid fa-chevron-down text-[10px] transition-transform ${showBatchMenu ? 'rotate-180' : ''}`}></i>
              </button>

              {/* Dropdown Menu */}
              {showBatchMenu && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-slate-900 border border-slate-700/80 rounded-2xl shadow-2xl z-50 overflow-hidden divide-y divide-slate-800/80 p-1.5 animate-in fade-in zoom-in-95 duration-150">
                  {/* Coleção Section */}
                  <div className="p-1.5 space-y-1">
                    <span className="text-[10px] font-black uppercase tracking-wider text-purple-400 px-2.5 block">
                      Coleção Pessoal
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const target = filteredCards.length > 0 ? filteredCards : cards;
                        openBatchModal(
                          'collection',
                          target,
                          'Adicionar Todas à Coleção',
                          `Adicionar 1 cópia de todas as ${target.length} cartas ${filteredCards.length !== cards.length ? 'filtradas' : 'desta coleção'} à sua coleção.`,
                          { onlyMissingOption: true, onlyMissingChecked: false }
                        );
                      }}
                      className="w-full text-left px-2.5 py-2 rounded-xl text-xs text-slate-200 hover:text-white hover:bg-slate-800/80 flex items-center gap-2.5 transition-colors cursor-pointer"
                    >
                      <i className="fa-solid fa-plus text-emerald-400 w-4 text-center"></i>
                      <div>
                        <div className="font-semibold">Adicionar todas à coleção</div>
                        <div className="text-[10px] text-slate-400">
                          {filteredCards.length !== cards.length ? `${filteredCards.length} cartas filtradas` : `${cards.length} cartas do set`}
                        </div>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        const missingCards = (filteredCards.length > 0 ? filteredCards : cards).filter(
                          c => !getCardOwnedInfo(c).isOwned
                        );
                        openBatchModal(
                          'collection',
                          missingCards,
                          'Adicionar Apenas Faltantes à Coleção',
                          `Adicionar 1 cópia apenas das ${missingCards.length} cartas que você ainda não possui nesta coleção.`,
                          { onlyMissingOption: false, onlyMissingChecked: true }
                        );
                      }}
                      className="w-full text-left px-2.5 py-2 rounded-xl text-xs text-slate-200 hover:text-white hover:bg-slate-800/80 flex items-center gap-2.5 transition-colors cursor-pointer"
                    >
                      <i className="fa-solid fa-circle-check text-emerald-400 w-4 text-center"></i>
                      <div>
                        <div className="font-semibold">Adicionar apenas faltantes</div>
                        <div className="text-[10px] text-slate-400">
                          {collectionStats.missingCount} cartas restantes
                        </div>
                      </div>
                    </button>
                  </div>

                  {/* Wishlist Section */}
                  <div className="p-1.5 space-y-1">
                    <span className="text-[10px] font-black uppercase tracking-wider text-rose-400 px-2.5 block">
                      Lista de Desejos
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const target = (filteredCards.length > 0 ? filteredCards : cards).filter(
                          c => !isCardInWishlist(c)
                        );
                        openBatchModal(
                          'wishlist',
                          target,
                          'Adicionar Todas à Wishlist',
                          `Adicionar as ${target.length} cartas que ainda não estão na sua lista de desejos.`,
                          { onlyMissingOption: false }
                        );
                      }}
                      className="w-full text-left px-2.5 py-2 rounded-xl text-xs text-slate-200 hover:text-white hover:bg-slate-800/80 flex items-center gap-2.5 transition-colors cursor-pointer"
                    >
                      <i className="fa-solid fa-heart text-rose-400 w-4 text-center"></i>
                      <div>
                        <div className="font-semibold">Adicionar todas à Wishlist</div>
                        <div className="text-[10px] text-slate-400">
                          Cartas que você ainda não favoritou
                        </div>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        const missingWishlist = (filteredCards.length > 0 ? filteredCards : cards).filter(
                          c => !getCardOwnedInfo(c).isOwned && !isCardInWishlist(c)
                        );
                        openBatchModal(
                          'wishlist',
                          missingWishlist,
                          'Adicionar Faltantes à Wishlist',
                          `Adicionar à Wishlist as ${missingWishlist.length} cartas que você ainda não possui e não favoritou.`,
                          { onlyMissingOption: false }
                        );
                      }}
                      className="w-full text-left px-2.5 py-2 rounded-xl text-xs text-slate-200 hover:text-white hover:bg-slate-800/80 flex items-center gap-2.5 transition-colors cursor-pointer"
                    >
                      <i className="fa-regular fa-heart text-rose-400 w-4 text-center"></i>
                      <div>
                        <div className="font-semibold">Adicionar faltantes à Wishlist</div>
                        <div className="text-[10px] text-slate-400">
                          Faltando na coleção e fora da wishlist
                        </div>
                      </div>
                    </button>
                  </div>

                  {/* Offerlist (Trocas) Section */}
                  <div className="p-1.5 space-y-1">
                    <span className="text-[10px] font-black uppercase tracking-wider text-amber-400 px-2.5 block">
                      Lista de Trocas (Offerlist)
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const target = filteredCards.length > 0 ? filteredCards : cards;
                        openBatchModal(
                          'offerlist',
                          target,
                          'Adicionar Todas à Offerlist',
                          `Adicionar 1 cópia de todas as ${target.length} cartas ${filteredCards.length !== cards.length ? 'filtradas' : 'desta coleção'} à sua lista de trocas.`,
                          { onlyMissingOption: true, onlyMissingChecked: false }
                        );
                      }}
                      className="w-full text-left px-2.5 py-2 rounded-xl text-xs text-slate-200 hover:text-white hover:bg-slate-800/80 flex items-center gap-2.5 transition-colors cursor-pointer"
                    >
                      <i className="fa-solid fa-arrow-right-arrow-left text-amber-400 w-4 text-center"></i>
                      <div>
                        <div className="font-semibold">Adicionar todas à Offerlist</div>
                        <div className="text-[10px] text-slate-400">
                          {filteredCards.length !== cards.length ? `${filteredCards.length} cartas filtradas` : `${cards.length} cartas do set`}
                        </div>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        const ownedCards = (filteredCards.length > 0 ? filteredCards : cards).filter(
                          c => getCardOwnedInfo(c).isOwned
                        );
                        openBatchModal(
                          'offerlist',
                          ownedCards,
                          'Adicionar Cartas Possuídas à Offerlist',
                          `Adicionar à lista de trocas apenas as ${ownedCards.length} cartas que você possui nesta coleção para disponibilizar a outros usuários.`,
                          { onlyMissingOption: false, onlyMissingChecked: true }
                        );
                      }}
                      className="w-full text-left px-2.5 py-2 rounded-xl text-xs text-slate-200 hover:text-white hover:bg-slate-800/80 flex items-center gap-2.5 transition-colors cursor-pointer"
                    >
                      <i className="fa-solid fa-handshake text-amber-400 w-4 text-center"></i>
                      <div>
                        <div className="font-semibold">Adicionar cartas possuídas</div>
                        <div className="text-[10px] text-slate-400">
                          {collectionStats.uniqueOwnedCount} cartas que você possui no set
                        </div>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        const notInOfferlist = (filteredCards.length > 0 ? filteredCards : cards).filter(
                          c => !isCardInOfferlist(c)
                        );
                        openBatchModal(
                          'offerlist',
                          notInOfferlist,
                          'Adicionar Não Listadas à Offerlist',
                          `Adicionar à Offerlist as ${notInOfferlist.length} cartas que ainda não estão cadastradas na sua lista de trocas.`,
                          { onlyMissingOption: false }
                        );
                      }}
                      className="w-full text-left px-2.5 py-2 rounded-xl text-xs text-slate-200 hover:text-white hover:bg-slate-800/80 flex items-center gap-2.5 transition-colors cursor-pointer"
                    >
                      <i className="fa-regular fa-clone text-amber-400 w-4 text-center"></i>
                      <div>
                        <div className="font-semibold">Adicionar ainda não listadas</div>
                        <div className="text-[10px] text-slate-400">
                          Cartas fora da sua lista de trocas
                        </div>
                      </div>
                    </button>
                  </div>

                  {/* Manual Multi-Select Toggle */}
                  <div className="p-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        setIsSelectionMode(prev => !prev);
                        setShowBatchMenu(false);
                      }}
                      className={`w-full text-left px-2.5 py-2 rounded-xl text-xs font-semibold flex items-center gap-2.5 transition-colors cursor-pointer ${
                        isSelectionMode
                          ? 'bg-purple-950/60 text-purple-300 border border-purple-500/40'
                          : 'text-slate-200 hover:text-white hover:bg-slate-800/80'
                      }`}
                    >
                      <i className={`fa-solid ${isSelectionMode ? 'fa-square-check text-purple-400' : 'fa-list-check text-slate-400'} w-4 text-center`}></i>
                      <div>
                        <div>{isSelectionMode ? 'Desativar Modo de Seleção' : 'Modo de Seleção Manual'}</div>
                        <div className="text-[10px] text-slate-400">
                          {isSelectionMode ? 'Clique para desmarcar' : 'Marque cartas manualmente'}
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Ownership Filter Pills: Todas / Na Coleção / Faltando */}
        <div className="flex items-center gap-2 pt-1 border-t border-slate-800/60 overflow-x-auto pb-1 text-xs">
          <button
            onClick={() => setOwnershipFilter('all')}
            className={`px-3 py-1.5 rounded-xl font-bold whitespace-nowrap transition-all ${
              ownershipFilter === 'all'
                ? 'bg-purple-600 text-white shadow-sm shadow-purple-600/30'
                : 'bg-slate-950/60 text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-slate-800/80'
            }`}
          >
            Todas as Cartas ({cards.length})
          </button>

          <button
            onClick={() => setOwnershipFilter('owned')}
            className={`px-3 py-1.5 rounded-xl font-bold whitespace-nowrap flex items-center gap-1.5 transition-all ${
              ownershipFilter === 'owned'
                ? 'bg-emerald-600 text-white shadow-sm shadow-emerald-600/30'
                : 'bg-slate-950/60 text-emerald-400/90 hover:text-emerald-300 hover:bg-emerald-950/20 border border-emerald-900/30'
            }`}
          >
            <i className="fa-solid fa-circle-check text-[11px]"></i>
            <span>Na Minha Coleção ({collectionStats.uniqueOwnedCount})</span>
          </button>

          <button
            onClick={() => setOwnershipFilter('missing')}
            className={`px-3 py-1.5 rounded-xl font-bold whitespace-nowrap flex items-center gap-1.5 transition-all ${
              ownershipFilter === 'missing'
                ? 'bg-amber-600 text-white shadow-sm shadow-amber-600/30'
                : 'bg-slate-950/60 text-amber-400/90 hover:text-amber-300 hover:bg-amber-950/20 border border-amber-900/30'
            }`}
          >
            <i className="fa-regular fa-circle-xmark text-[11px]"></i>
            <span>Faltando ({collectionStats.missingCount})</span>
          </button>

          <div className="ml-auto text-slate-400 text-xs hidden md:block whitespace-nowrap">
            Exibindo <strong className="text-white">{filteredCards.length}</strong> de {cards.length} cartas
          </div>
        </div>
      </div>

      {/* Quick Filters Reused Component */}
      <QuickFilters
        gameSlug={currentGame?.slug || currentGameId}
        activeFilters={activeQuickFilters}
        setActiveFilters={setActiveQuickFilters}
      />

      {/* Cards Presentation Area */}
      <div ref={cardsContainerRef} className="scroll-mt-6">
      {loadingCards ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="bg-slate-900/60 border border-slate-800/80 rounded-2xl aspect-[7/10] animate-pulse p-3 flex flex-col justify-end"
            >
              <div className="h-4 bg-slate-800 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-slate-800 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      ) : filteredCards.length === 0 ? (
        <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-12 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-purple-950/40 border border-purple-500/20 flex items-center justify-center mx-auto text-purple-400 text-2xl">
            <i className="fa-solid fa-layer-group"></i>
          </div>
          <h3 className="text-lg font-bold text-white">Nenhuma carta encontrada</h3>
          <p className="text-xs md:text-sm text-slate-400 max-w-md mx-auto">
            {ownershipFilter === 'owned'
              ? 'Você ainda não possui nenhuma carta desta coleção no seu perfil. Explore o set completo para começar a colecionar!'
              : ownershipFilter === 'missing'
              ? 'Parabéns! Você já possui todas as cartas desta coleção.'
              : searchQuery
              ? `Nenhuma carta corresponde à busca "${searchQuery}". Tente limpar os filtros.`
              : 'Não foi possível carregar as cartas desta coleção no momento.'}
          </p>

          {(searchQuery || ownershipFilter !== 'all' || rarityFilter !== 'all' || Object.keys(activeQuickFilters).length > 0) && (
            <button
              onClick={() => {
                setSearchQuery('');
                setOwnershipFilter('all');
                setRarityFilter('all');
                setActiveQuickFilters({});
              }}
              className="px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-all shadow-md shadow-purple-600/30 cursor-pointer"
            >
              Limpar Filtros e Ver Todas
            </button>
          )}
        </div>
      ) : (
        <>
          {viewMode === 'grid' ? (
        /* GRID VIEW */
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
          {paginatedCards.map((card, idx) => {
            const ownedInfo = getCardOwnedInfo(card);
            const isOwned = ownedInfo.isOwned && ownedInfo.quantity > 0;
            const currentCardId = String(card.id || card.external_id || '');
            const cardIdentifier = getCardKey(card);
            const isSelected = selectedCardKeys.has(cardIdentifier);
            const isAdding = Boolean(addingCardId && addingCardId === currentCardId);
            const isInWishlist = isCardInWishlist(card);
            const isWishlistLoading = Boolean(wishlistLoadingCardId && wishlistLoadingCardId === currentCardId);
            const cardKey = card.id ? `card-id-${card.id}` : `card-ext-${card.external_id || card.code || idx}`;

            return (
              <div
                key={cardKey}
                onClick={() => {
                  if (isSelectionMode) {
                    toggleSelectCard(card);
                  } else {
                    setSelectedCard(card);
                  }
                }}
                className={`group relative bg-slate-900/90 rounded-2xl border transition-all duration-200 p-2.5 flex flex-col justify-between cursor-pointer hover:-translate-y-1 hover:shadow-xl ${
                  isSelected
                    ? 'border-purple-500 ring-2 ring-purple-500/80 bg-purple-950/20 shadow-purple-950/40'
                    : isOwned
                    ? 'border-emerald-500/40 hover:border-emerald-400 bg-emerald-950/10 hover:shadow-emerald-950/30'
                    : 'border-slate-800 hover:border-purple-500/40 hover:shadow-purple-950/20'
                }`}
              >
                {/* Selection Checkbox Overlay */}
                {isSelectionMode && (
                  <div
                    className="absolute top-2 left-2 z-20"
                    onClick={(e) => toggleSelectCard(card, e)}
                  >
                    <div
                      className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold border transition-all shadow-md ${
                        isSelected
                          ? 'bg-purple-600 border-purple-400 text-white shadow-purple-600/50'
                          : 'bg-slate-950/90 border-slate-700 text-transparent hover:border-purple-400'
                      }`}
                    >
                      <i className="fa-solid fa-check text-[10px]"></i>
                    </div>
                  </div>
                )}

                {/* Top Status Row: Code and ID + Ownership Badge */}
                <div className={`flex items-center justify-between gap-1 mb-2 ${isSelectionMode ? 'pl-7' : ''}`}>
                  <div className="flex items-center gap-1 min-w-0 flex-wrap">
                    {card.code && (
                      <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-950/90 text-blue-300 border border-blue-500/30 truncate" title={`Código: ${card.code}`}>
                        {card.code}
                      </span>
                    )}
                    {!card.code && !card.id && (
                      <span className="font-mono text-[10px] text-slate-500">---</span>
                    )}
                  </div>

                  {/* Owned Status Tag */}
                  {user && (
                    <span
                      className={`text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1 ${
                        isOwned
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                          : 'bg-slate-950/70 text-slate-500 border border-slate-800'
                      }`}
                    >
                      {isOwned ? (
                        <>
                          <i className="fa-solid fa-check text-[9px]"></i>
                          <span>{ownedInfo.quantity}x</span>
                        </>
                      ) : (
                        <span>Falta</span>
                      )}
                    </span>
                  )}
                </div>

                {/* Card Artwork */}
                <div className="relative aspect-[7/10] w-full rounded-xl overflow-hidden bg-slate-950 mb-2 border border-slate-800/60">
                  <CardImage
                    src={card.imageUrl || card.image_url}
                    alt={card.name}
                    gameId={currentGameId}
                    objectFit="cover"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />

                  {/* Rarity Pill floating on image */}
                  {card.rarity && (
                    <div className="absolute bottom-1.5 left-1.5">
                      <span className="font-mono text-[9px] font-bold px-1.5 py-0.5 rounded-md bg-black/80 backdrop-blur-sm text-purple-300 border border-purple-500/30">
                        {card.rarity}
                      </span>
                    </div>
                  )}

                  {/* Quick Action Overlay on Hover: Wishlist (left) + Collection Add (right) */}
                  {user && (
                    <div className="absolute top-1.5 right-1.5 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-all z-10">
                      {/* Heart Wishlist Button */}
                      <button
                        type="button"
                        onClick={(e) => handleToggleWishlist(card, e)}
                        disabled={isWishlistLoading}
                        title={isInWishlist ? "Remover da lista de desejos" : "Adicionar à lista de desejos"}
                        className={`w-7 h-7 rounded-lg bg-slate-950/90 border flex items-center justify-center text-xs transition-all shadow-md cursor-pointer ${
                          isInWishlist
                            ? 'border-rose-500/80 text-rose-500 hover:bg-rose-950/80 hover:border-rose-400'
                            : 'border-slate-700/80 text-slate-300 hover:text-rose-400 hover:border-rose-500/60 hover:bg-slate-900'
                        }`}
                      >
                        {isWishlistLoading ? (
                          <i className="fa-solid fa-spinner fa-spin text-[10px]"></i>
                        ) : (
                          <i className={`${isInWishlist ? 'fa-solid text-rose-500' : 'fa-regular'} fa-heart text-[11px]`}></i>
                        )}
                      </button>

                      {/* Quick Add Overlay */}
                      <button
                        type="button"
                        onClick={(e) => handleQuickAdd(card, e)}
                        disabled={isAdding}
                        title="Adicionar cópia à minha coleção"
                        className="w-7 h-7 rounded-lg bg-slate-950/90 hover:bg-emerald-600 text-slate-300 hover:text-white border border-slate-700/80 hover:border-emerald-500 flex items-center justify-center text-xs transition-all shadow-md cursor-pointer"
                      >
                        {isAdding ? (
                          <i className="fa-solid fa-spinner fa-spin text-[10px]"></i>
                        ) : (
                          <i className="fa-solid fa-plus text-[10px]"></i>
                        )}
                      </button>
                    </div>
                  )}
                </div>

                {/* Card Info Footer */}
                <div className="space-y-1">
                  <h4 className="text-xs font-bold text-white group-hover:text-purple-300 transition-colors line-clamp-1 leading-snug">
                    {card.name}
                  </h4>
                  <div className="flex items-center justify-between text-[11px] text-slate-400">
                    <span className="truncate text-slate-400">
                      {card.type || card.cardType || card.color || ''}
                    </span>
                    {settings.showCardPrices && card.price > 0 && (
                      <span className="font-semibold text-emerald-400 shrink-0">
                        {formatPrice(card.price)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* CHECKLIST / TABLE VIEW */
        <div className="bg-slate-900/90 border border-slate-800 rounded-2xl overflow-hidden shadow-lg">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs text-slate-300">
              <thead className="bg-slate-950/80 text-[11px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-800">
                <tr>
                  <th className="py-3 px-4 w-16 text-center">
                    {isSelectionMode ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const allFilteredSelected = filteredCards.length > 0 && filteredCards.every(c => selectedCardKeys.has(getCardKey(c)));
                          if (allFilteredSelected) clearSelection();
                          else selectAllFiltered();
                        }}
                        title={filteredCards.length > 0 && filteredCards.every(c => selectedCardKeys.has(getCardKey(c))) ? "Desmarcar todas" : "Selecionar todas exibidas"}
                        className={`w-5 h-5 rounded-md flex items-center justify-center border transition-all mx-auto cursor-pointer ${
                          filteredCards.length > 0 && filteredCards.every(c => selectedCardKeys.has(getCardKey(c)))
                            ? 'bg-purple-600 border-purple-400 text-white'
                            : 'bg-slate-950 border-slate-700 text-transparent hover:border-purple-400'
                        }`}
                      >
                        <i className="fa-solid fa-check text-[9px]"></i>
                      </button>
                    ) : (
                      'Status'
                    )}
                  </th>
                  <th className="py-3 px-3 w-14">Arte</th>
                  <th className="py-3 px-4 w-28">Código</th>
                  <th className="py-3 px-4">Nome da Carta</th>
                  <th className="py-3 px-4 w-24">Raridade</th>
                  <th className="py-3 px-4 w-28">Tipo / Cor</th>
                  <th className="py-3 px-4 w-24 text-right">Preço Est.</th>
                  <th className="py-3 px-4 w-24 text-center">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {paginatedCards.map((card, idx) => {
                  const ownedInfo = getCardOwnedInfo(card);
                  const isOwned = ownedInfo.isOwned && ownedInfo.quantity > 0;
                  const currentCardId = String(card.id || card.external_id || '');
                  const cardIdentifier = getCardKey(card);
                  const isSelected = selectedCardKeys.has(cardIdentifier);
                  const isAdding = Boolean(addingCardId && addingCardId === currentCardId);
                  const isInWishlist = isCardInWishlist(card);
                  const isWishlistLoading = Boolean(wishlistLoadingCardId && wishlistLoadingCardId === currentCardId);
                  const cardKey = card.id ? `card-id-${card.id}` : `card-ext-${card.external_id || card.code || idx}`;

                  return (
                    <tr
                      key={cardKey}
                      onClick={() => {
                        if (isSelectionMode) {
                          toggleSelectCard(card);
                        } else {
                          setSelectedCard(card);
                        }
                      }}
                      className={`hover:bg-slate-800/40 cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-purple-950/30'
                          : isOwned
                          ? 'bg-emerald-950/10'
                          : ''
                      }`}
                    >
                      {/* Status Checkbox */}
                      <td className="py-2.5 px-4 text-center" onClick={(e) => {
                        if (isSelectionMode) toggleSelectCard(card, e);
                      }}>
                        {isSelectionMode ? (
                          <div
                            className={`w-5 h-5 rounded-md flex items-center justify-center border transition-all mx-auto ${
                              isSelected
                                ? 'bg-purple-600 border-purple-400 text-white shadow-sm'
                                : 'bg-slate-950 border-slate-700 text-transparent hover:border-purple-400'
                            }`}
                          >
                            <i className="fa-solid fa-check text-[9px]"></i>
                          </div>
                        ) : user ? (
                          isOwned ? (
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 font-bold text-[10px]">
                              {ownedInfo.quantity}x
                            </span>
                          ) : (
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-950 text-slate-600 border border-slate-800 text-[10px]">
                              <i className="fa-regular fa-circle"></i>
                            </span>
                          )
                        ) : (
                          <span className="text-slate-600">-</span>
                        )}
                      </td>

                      {/* Thumbnail */}
                      <td className="py-2 px-3">
                        <div className="w-9 h-12 rounded-md overflow-hidden bg-slate-950 border border-slate-800 shrink-0">
                          <CardImage
                            src={card.imageUrl || card.image_url}
                            alt={card.name}
                            gameId={currentGameId}
                            objectFit="cover"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </td>

                      {/* Code & ID */}
                      <td className="py-2.5 px-4 font-mono font-bold text-slate-200">
                        <div className="flex flex-col">
                          <span className="text-blue-300">{card.code || '---'}</span>
                          {card.id && String(card.id) !== String(card.code) && (
                            <span className="text-[10px] text-purple-400 font-normal">ID: {card.id}</span>
                          )}
                        </div>
                      </td>

                      {/* Name */}
                      <td className="py-2.5 px-4 font-semibold text-white">
                        {card.name}
                      </td>

                      {/* Rarity */}
                      <td className="py-2.5 px-4">
                        {card.rarity ? (
                          <span className="font-mono text-[10px] font-bold px-2 py-0.5 rounded bg-purple-950/60 text-purple-300 border border-purple-500/20">
                            {card.rarity}
                          </span>
                        ) : (
                          <span className="text-slate-600">-</span>
                        )}
                      </td>

                      {/* Type / Color */}
                      <td className="py-2.5 px-4 text-slate-400">
                        {card.type || card.color || '-'}
                      </td>

                      {/* Estimated Price */}
                      <td className="py-2.5 px-4 text-right font-medium text-emerald-400">
                        {settings.showCardPrices && card.price > 0
                          ? formatPrice(card.price)
                          : '-'}
                      </td>

                      {/* Action */}
                      <td className="py-2.5 px-4 text-center" onClick={e => e.stopPropagation()}>
                        {user && (
                          <div className="flex items-center justify-center gap-1.5">
                            {/* Heart Wishlist Button */}
                            <button
                              type="button"
                              onClick={(e) => handleToggleWishlist(card, e)}
                              disabled={isWishlistLoading}
                              title={isInWishlist ? "Remover da lista de desejos" : "Adicionar à lista de desejos"}
                              className={`w-7 h-7 rounded-lg bg-slate-800 border flex items-center justify-center text-xs transition-all cursor-pointer ${
                                isInWishlist
                                  ? 'border-rose-500/80 text-rose-500 hover:bg-rose-950/60'
                                  : 'border-slate-700 text-slate-300 hover:text-rose-400 hover:border-rose-500/60'
                              }`}
                            >
                              {isWishlistLoading ? (
                                <i className="fa-solid fa-spinner fa-spin text-[10px]"></i>
                              ) : (
                                <i className={`${isInWishlist ? 'fa-solid text-rose-500' : 'fa-regular'} fa-heart text-[11px]`}></i>
                              )}
                            </button>

                            {/* Add Button */}
                            <button
                              type="button"
                              onClick={() => handleQuickAdd(card)}
                              disabled={isAdding}
                              title="Adicionar cópia à coleção"
                              className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-emerald-600 text-slate-300 hover:text-white border border-slate-700 hover:border-emerald-500 text-xs font-semibold transition-all inline-flex items-center gap-1 cursor-pointer"
                            >
                              {isAdding ? (
                                <i className="fa-solid fa-spinner fa-spin text-[10px]"></i>
                              ) : (
                                <>
                                  <i className="fa-solid fa-plus text-[9px]"></i>
                                  <span>Add</span>
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

          {/* Pagination Controls */}
          {filteredCards.length > 0 && (
            <div className="mt-6 pt-5 border-t border-slate-800/80 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-xs text-slate-400 font-medium">
                Mostrando <span className="font-bold text-white">{filteredCards.length === 0 ? 0 : (currentPage - 1) * cardsPerPage + 1}</span> a <span className="font-bold text-white">{Math.min(filteredCards.length, currentPage * cardsPerPage)}</span> de <span className="font-bold text-white">{filteredCards.length}</span> cartas
              </div>

              {totalPages > 1 && (
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 text-slate-300 hover:text-white hover:border-purple-500/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-xs font-bold flex items-center gap-1.5 cursor-pointer"
                    aria-label="Página anterior"
                  >
                    <i className="fa-solid fa-chevron-left text-[10px]"></i>
                    <span className="hidden sm:inline">Anterior</span>
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }).map((_, idx) => {
                      const pageNum = idx + 1;
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNum}
                            type="button"
                            onClick={() => handlePageChange(pageNum)}
                            className={`min-w-8 h-8 px-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                              currentPage === pageNum
                                ? 'bg-purple-600 text-white shadow-md shadow-purple-600/30'
                                : 'bg-slate-900 border border-slate-800 text-slate-400 hover:text-white hover:border-slate-700'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      } else if (
                        pageNum === currentPage - 2 ||
                        pageNum === currentPage + 2
                      ) {
                        return (
                          <span key={pageNum} className="text-slate-600 text-xs px-1">
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 text-slate-300 hover:text-white hover:border-purple-500/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-xs font-bold flex items-center gap-1.5 cursor-pointer"
                    aria-label="Próxima página"
                  >
                    <span className="hidden sm:inline">Próxima</span>
                    <i className="fa-solid fa-chevron-right text-[10px]"></i>
                  </button>
                </div>
              )}

              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span>Por página:</span>
                <select
                  value={cardsPerPage}
                  onChange={e => {
                    setCardsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="bg-slate-900 border border-slate-800 rounded-lg px-2.5 py-1 text-slate-200 text-xs font-semibold focus:outline-none focus:border-purple-500 cursor-pointer"
                >
                  <option value={24}>24</option>
                  <option value={36}>36</option>
                  <option value={48}>48</option>
                  <option value={96}>96</option>
                  <option value={9999}>Todas</option>
                </select>
              </div>
            </div>
          )}
        </>
      )}
      </div>
    </>
  )}

      {/* Floating Selection Bar */}
      <AnimatePresence>
        {isSelectionMode && (
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 max-w-2xl w-[92%] sm:w-auto bg-slate-900/95 border border-purple-500/40 shadow-2xl shadow-black/80 backdrop-blur-md rounded-2xl px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-xs"
          >
            <div className="flex items-center gap-2.5">
              <span className="px-2.5 py-1 rounded-xl bg-purple-950 text-purple-300 border border-purple-500/40 font-bold">
                {selectedCardKeys.size} {selectedCardKeys.size === 1 ? 'selecionada' : 'selecionadas'}
              </span>

              <button
                type="button"
                onClick={() => {
                  const allSelected = filteredCards.length > 0 && filteredCards.every(c => selectedCardKeys.has(getCardKey(c)));
                  if (allSelected) clearSelection();
                  else selectAllFiltered();
                }}
                className="text-slate-300 hover:text-white font-semibold underline underline-offset-2 transition-colors cursor-pointer"
              >
                {filteredCards.length > 0 && filteredCards.every(c => selectedCardKeys.has(getCardKey(c)))
                  ? 'Desmarcar tudo'
                  : `Selecionar tudo (${filteredCards.length})`}
              </button>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={selectedCardKeys.size === 0}
                onClick={() => {
                  const selCards = cards.filter(c => selectedCardKeys.has(getCardKey(c)));
                  openBatchModal(
                    'collection',
                    selCards,
                    'Adicionar Selecionadas à Coleção',
                    `Adicionar 1 cópia de ${selCards.length} carta(s) selecionada(s) à sua coleção.`,
                    { onlyMissingOption: true, onlyMissingChecked: false }
                  );
                }}
                className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold flex items-center gap-1.5 transition-all shadow-sm cursor-pointer"
              >
                <i className="fa-solid fa-plus text-[10px]"></i>
                <span>Coleção ({selectedCardKeys.size})</span>
              </button>

              <button
                type="button"
                disabled={selectedCardKeys.size === 0}
                onClick={() => {
                  const selCards = cards.filter(c => selectedCardKeys.has(getCardKey(c)));
                  openBatchModal(
                    'wishlist',
                    selCards,
                    'Adicionar Selecionadas à Wishlist',
                    `Adicionar ${selCards.length} carta(s) selecionada(s) à sua Lista de Desejos.`,
                    { onlyMissingOption: false }
                  );
                }}
                className="px-3 py-1.5 rounded-xl bg-rose-600 hover:bg-rose-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold flex items-center gap-1.5 transition-all shadow-sm cursor-pointer"
              >
                <i className="fa-solid fa-heart text-[10px]"></i>
                <span>Wishlist ({selectedCardKeys.size})</span>
              </button>

              <button
                type="button"
                disabled={selectedCardKeys.size === 0}
                onClick={() => {
                  const selCards = cards.filter(c => selectedCardKeys.has(getCardKey(c)));
                  openBatchModal(
                    'offerlist',
                    selCards,
                    'Adicionar Selecionadas à Offerlist',
                    `Adicionar ${selCards.length} carta(s) selecionada(s) à sua Lista de Ofertas (Trocas).`,
                    { onlyMissingOption: false }
                  );
                }}
                className="px-3 py-1.5 rounded-xl bg-amber-600 hover:bg-amber-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold flex items-center gap-1.5 transition-all shadow-sm cursor-pointer"
              >
                <i className="fa-solid fa-arrow-right-arrow-left text-[10px]"></i>
                <span>Offerlist ({selectedCardKeys.size})</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsSelectionMode(false);
                  clearSelection();
                }}
                className="px-2.5 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors cursor-pointer"
                title="Sair do modo seleção"
              >
                <i className="fa-solid fa-xmark text-xs"></i>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Batch Operation Modal */}
      <AnimatePresence>
        {batchModal && batchModal.isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-slate-900 border border-slate-700 rounded-3xl p-6 sm:p-7 max-w-md w-full shadow-2xl space-y-5 text-slate-100 relative"
            >
              {/* Header */}
              <div className="flex items-start gap-4">
                <div
                  className={`w-12 h-12 rounded-2xl flex items-center justify-center text-xl shrink-0 ${
                    batchModal.type === 'collection'
                      ? 'bg-purple-950/80 text-purple-400 border border-purple-500/40'
                      : batchModal.type === 'wishlist'
                      ? 'bg-rose-950/80 text-rose-400 border border-rose-500/40'
                      : batchModal.type === 'offerlist'
                      ? 'bg-amber-950/80 text-amber-400 border border-amber-500/40'
                      : 'bg-amber-950/80 text-amber-400 border border-amber-500/40'
                  }`}
                >
                  <i
                    className={`fa-solid ${
                      batchModal.type === 'collection'
                        ? 'fa-layer-group'
                        : batchModal.type === 'wishlist'
                        ? 'fa-heart'
                        : batchModal.type === 'offerlist'
                        ? 'fa-arrow-right-arrow-left'
                        : 'fa-trash-can'
                    }`}
                  ></i>
                </div>

                <div className="space-y-1 flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-white leading-snug">
                    {batchModal.title}
                  </h3>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    {batchModal.description}
                  </p>
                </div>
              </div>

              {/* Progress State */}
              {batchLoading && (
                <div className="space-y-3 py-3 border-y border-slate-800">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-300 font-semibold flex items-center gap-2">
                      <i className="fa-solid fa-spinner fa-spin text-purple-400"></i>
                      <span>Sincronizando e adicionando...</span>
                    </span>
                    <span className="font-mono text-purple-400 font-bold">
                      {batchProgress?.percent || 0}%
                    </span>
                  </div>

                  <div className="w-full bg-slate-950 rounded-full h-3 overflow-hidden border border-slate-800">
                    <div
                      className="bg-gradient-to-r from-purple-500 to-emerald-400 h-full transition-all duration-300 rounded-full"
                      style={{ width: `${batchProgress?.percent || 0}%` }}
                    />
                  </div>

                  <div className="text-[11px] text-slate-400 text-center">
                    Processadas {batchProgress?.current || 0} de {batchProgress?.total || 0} cartas
                  </div>
                </div>
              )}

              {/* Result State */}
              {!batchLoading && batchResult && (
                <div className="bg-slate-950/80 border border-slate-800 rounded-2xl p-4 space-y-3">
                  <div className="flex items-center gap-3 text-emerald-400">
                    <i className="fa-solid fa-circle-check text-xl shrink-0"></i>
                    <span className="font-bold text-sm text-white">Operação Concluída com Sucesso!</span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-center text-xs">
                    {'added' in batchResult && (
                      <div className="bg-slate-900 border border-slate-800 rounded-xl p-2.5">
                        <span className="text-slate-400 block text-[10px]">Adicionadas</span>
                        <span className="text-base font-black text-emerald-400">{batchResult.added}</span>
                      </div>
                    )}
                    {'skipped' in batchResult && (
                      <div className="bg-slate-900 border border-slate-800 rounded-xl p-2.5">
                        <span className="text-slate-400 block text-[10px]">Já Existiam</span>
                        <span className="text-base font-black text-slate-300">{batchResult.skipped}</span>
                      </div>
                    )}
                    {'updated' in batchResult && batchResult.updated > 0 && (
                      <div className="bg-slate-900 border border-slate-800 rounded-xl p-2.5">
                        <span className="text-slate-400 block text-[10px]">Atualizadas</span>
                        <span className="text-base font-black text-purple-400">{batchResult.updated}</span>
                      </div>
                    )}
                    {'removed' in batchResult && (
                      <div className="bg-slate-900 border border-slate-800 rounded-xl p-2.5">
                        <span className="text-slate-400 block text-[10px]">Removidas</span>
                        <span className="text-base font-black text-amber-400">{batchResult.removed}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Config Options when Idle */}
              {!batchLoading && !batchResult && (
                <div className="space-y-3">
                  <div className="bg-slate-950/70 border border-slate-800 rounded-2xl p-3.5 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-400">Total de cartas selecionadas:</span>
                      <strong className="text-white font-mono text-sm">{batchModal.cardsToProcess.length}</strong>
                    </div>

                    {(batchModal.type === 'collection' || batchModal.type === 'offerlist') && batchModal.onlyMissingOption && (
                      <label className="flex items-center gap-2 pt-2 border-t border-slate-800/80 text-xs text-slate-300 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={Boolean(batchModal.onlyMissingChecked)}
                          onChange={e =>
                            setBatchModal(prev =>
                              prev ? { ...prev, onlyMissingChecked: e.target.checked } : null
                            )
                          }
                          className="rounded border-slate-700 bg-slate-900 text-purple-600 focus:ring-purple-500 w-4 h-4 cursor-pointer"
                        />
                        <span>
                          {batchModal.type === 'collection'
                            ? 'Apenas adicionar cartas que ainda não possuo no set'
                            : 'Apenas adicionar cartas que ainda não estão na minha lista de trocas'}
                        </span>
                      </label>
                    )}
                  </div>

                  <p className="text-[11px] text-slate-400 leading-normal">
                    {batchModal.type === 'collection'
                      ? 'As cartas serão sincronizadas com o catálogo oficial e adicionadas à sua coleção com quantidade 1.'
                      : batchModal.type === 'wishlist'
                      ? 'As cartas serão salvas na sua lista de desejos para que você acompanhe suas buscas e negociações.'
                      : 'As cartas serão adicionadas à sua lista de ofertas (trocas) para ficarem disponíveis em matches e negociações do CardLink.'}
                  </p>
                </div>
              )}

              {/* Actions Footer */}
              <div className="flex items-center justify-end gap-2.5 pt-2">
                {!batchLoading && !batchResult && (
                  <>
                    <button
                      type="button"
                      onClick={() => setBatchModal(null)}
                      className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold transition-colors cursor-pointer"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={executeBatchAction}
                      className={`px-5 py-2 rounded-xl text-white text-xs font-bold transition-all shadow-lg cursor-pointer flex items-center gap-1.5 ${
                        batchModal.type === 'wishlist'
                          ? 'bg-rose-600 hover:bg-rose-500 shadow-rose-600/30'
                          : batchModal.type === 'offerlist'
                          ? 'bg-amber-600 hover:bg-amber-500 shadow-amber-600/30'
                          : 'bg-purple-600 hover:bg-purple-500 shadow-purple-600/30'
                      }`}
                    >
                      <i className="fa-solid fa-bolt text-[11px]"></i>
                      <span>Confirmar e Adicionar ({batchModal.cardsToProcess.length})</span>
                    </button>
                  </>
                )}

                {batchResult && (
                  <button
                    type="button"
                    onClick={() => setBatchModal(null)}
                    className="w-full py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition-all shadow-md shadow-purple-600/30 cursor-pointer"
                  >
                    Fechar
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Card Detail Modal */}
      <AnimatePresence>
        {selectedCard && (
          <CardDetailModal
            card={selectedCard}
            cardsList={filteredCards}
            onSelectCard={card => setSelectedCard(card)}
            onClose={() => setSelectedCard(null)}
            gameName={currentGame?.name}
            onCardAdded={async (folderId) => {
              if (folderId === 'colecao' || folderId === 'cards') {
                await loadUserCards();
              }
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
export default CollectionCardsPage;
